import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  CircularProgress,
  Container,
  Divider,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import axios from "axios";
import { API_URL } from "../utils/api-config";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentService from "../services/AssignmentService";
import type { Assignment } from "../services/AssignmentService";
import { useSnackbar } from "notistack";

const damageTypes = ["Rayure", "Choc", "Manquant", "Casse"] as const;
type DamageType = (typeof damageTypes)[number] | "Cassé";

// Définition des faces de véhicule par défaut
const vehicleFaces = [
  {
    name: "Avant",
    url: "/images/vehicles/face.png",
    apiValue: "avant",
  },
  {
    name: "Arrière",
    url: "/images/vehicles/derriere.png",
    apiValue: "arriere",
  },
  {
    name: "Côté gauche",
    url: "/images/vehicles/gauche.png",
    apiValue: "coteConducteur",
  },
  {
    name: "Côté droit",
    url: "/images/vehicles/droite.png",
    apiValue: "cotePassager",
  },
  {
    name: "Toit",
    url: "/images/vehicles/toit.png",
    apiValue: "toit",
  },
] as const;

// Fonction pour obtenir les faces par défaut (pour usage dynamique)
const getDefaultVehicleFaces = () => [...vehicleFaces];

type VehicleFace = (typeof vehicleFaces)[number]["name"];

// Fonction pour convertir le nom d'une face frontend vers la valeur backend
const mapFrontendFaceToBackend = (face: string): string => {
  const faceObj = vehicleFaces.find((f) => f.name === face);
  return faceObj?.apiValue || face.toLowerCase();
};

// Fonction pour convertir la valeur backend vers le nom de face frontend
const mapBackendFaceToFrontend = (apiValue: string): string => {
  const faceObj = vehicleFaces.find((f) => f.apiValue === apiValue);
  return faceObj?.name || apiValue;
};

interface DamagePoint {
  x: number;
  y: number;
  type: DamageType;
  face: VehicleFace;
  driverId?: string;
}

interface DamageImage {
  url: string;
  damageType: DamageType;
  face: VehicleFace;
  template?: string;
  position?: { x: number; y: number };
  driverId?: string;
  points?: DamagePoint[];
}

interface DegradationState {
  count: number;
  details: {
    rayures: number;
    chocs: number;
    manquant: number;
    casse: number;
  };
  images: DamageImage[];
}

const ExteriorInspection = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isReturn = searchParams.get("return") === "true";
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const imageRef = useRef<HTMLImageElement>(null);

  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ✨ MODE D'INSPECTION (rapide, standard, complet)
  type InspectionMode = 'quick' | 'standard' | 'complete' | null;
  const [inspectionMode, setInspectionMode] = useState<InspectionMode>(null);
  const [showModeSelector, setShowModeSelector] = useState(true);

  // ✨ WORKFLOW RAS (Rien À Signaler)
  const [showRASConfirmation, setShowRASConfirmation] = useState(false);
  const [rasSubmitting, setRasSubmitting] = useState(false);

  // ✨ GESTION SIMPLE DES DIALOGUES
  // Un seul dialogue ouvert à la fois: 'modes' | 'ras' | null
  const [activeDialog, setActiveDialog] = useState<'modes' | 'ras' | null>('modes');
  
  // ✨ PHOTOS RÉELLES DU VÉHICULE (ou images par défaut)
  const [vehicleFaces, setVehicleFaces] = useState(() => getDefaultVehicleFaces());

  // État pour les dégradations
  const [degradations, setDegradations] = useState<DegradationState>({
    count: 0,
    details: {
      rayures: 0,
      chocs: 0,
      manquant: 0,
      casse: 0,
    },
    images: [],
  });

  // État pour la sélection de la face et du type de dégât
  const [selectedFace, setSelectedFace] = useState<string>("");
  const [selectedDamageType, setSelectedDamageType] = useState<string>("");
  const [isAddingDamage, setIsAddingDamage] = useState(false);

  // Auto-sélection du mode en fonction de l'historique
  useEffect(() => {
    const suggestInspectionMode = async () => {
      if (!id || inspectionMode !== null) return;
      
      try {
        const data = await AssignmentService.getById(id);
        // Si c'est le même chauffeur + même véhicule régulièrement, suggérer mode rapide
        // Pour l'instant, on laisse l'utilisateur choisir
        setShowModeSelector(true);
      } catch (err) {
        console.log("Impossible de suggérer un mode");
      }
    };
    
    suggestInspectionMode();
  }, [id, inspectionMode]);

  // Charger les détails de l'affectation
  useEffect(() => {
    const fetchAssignment = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await AssignmentService.getById(id);
        setAssignment(data);

        console.log(
          `[ExteriorInspection] Affectation récupérée (${
            isReturn ? "retour" : "entrée"
          }):`,
          data
        );

        // Charger les photos réelles du véhicule si disponibles
        try {
          const vehicleResponse = await axios.get(`${API_URL}/vehicles?plate=${data.vehiclePlate}`);
          const vehicle = vehicleResponse.data.find((v: any) => v.plate === data.vehiclePlate);
          
          if (vehicle?.inspectionPhotos) {
            const defaultFaces = getDefaultVehicleFaces();
            const updatedFaces = defaultFaces.map((face) => {
              let photoUrl = face.url;
              
              // Mapper les noms de faces vers les clés de inspectionPhotos
              if (face.apiValue === 'avant' && vehicle.inspectionPhotos.front) {
                photoUrl = vehicle.inspectionPhotos.front;
              } else if (face.apiValue === 'arriere' && vehicle.inspectionPhotos.back) {
                photoUrl = vehicle.inspectionPhotos.back;
              } else if (face.apiValue === 'coteConducteur' && vehicle.inspectionPhotos.driverSide) {
                photoUrl = vehicle.inspectionPhotos.driverSide;
              } else if (face.apiValue === 'cotePassager' && vehicle.inspectionPhotos.passengerSide) {
                photoUrl = vehicle.inspectionPhotos.passengerSide;
              }
              
              return { ...face, url: photoUrl };
            });
            
            setVehicleFaces(updatedFaces as any);
            console.log('[ExteriorInspection] Photos du véhicule chargées:', vehicle.inspectionPhotos);
          }
        } catch (vehicleErr) {
          console.log('[ExteriorInspection] Impossible de charger les photos du véhicule, utilisation des images par défaut');
        }

        // Initialiser les dégradations UNIQUEMENT si elles existent déjà pour cette phase spécifique
        if (isReturn && data.finalState?.degradations) {
          // Phase de retour - charger uniquement les dégradations de retour
          console.log(
            "[ExteriorInspection] Chargement des dégradations de RETOUR"
          );

          const images = (data.finalState.degradations.images || []).map(
            (img) => {
              // Si l'image contient déjà les nouvelles propriétés
              if (img.template && img.position) {
                // Convertir la valeur API de la face vers le nom frontend
                const frontendFace = mapBackendFaceToFrontend(img.face);

                return {
                  url: img.url,
                  damageType: img.damageType as DamageType,
                  face: frontendFace as VehicleFace,
                  template: img.template,
                  position: img.position,
                  driverId: img.driverId || data.driverId,
                  points: img.points || [
                    {
                      x: img.position.x,
                      y: img.position.y,
                      type: img.damageType as DamageType,
                      face: frontendFace as VehicleFace,
                      driverId: img.driverId || data.driverId,
                    },
                  ],
                };
              }

              // Conversion du format ancien
              // Convertir la valeur API de la face vers le nom frontend
              const frontendFace = mapBackendFaceToFrontend(img.face);

              return {
                url: img.url,
                damageType: img.damageType as DamageType,
                face: frontendFace as VehicleFace,
                template:
                  vehicleFaces.find((f) => f.name === frontendFace)?.url || "",
                position: img.points?.[0]
                  ? { x: img.points[0].x, y: img.points[0].y }
                  : undefined,
                driverId: data.driverId,
                points: (img.points || []).map((point) => ({
                  x: point.x,
                  y: point.y,
                  type: point.type as DamageType,
                  face: mapBackendFaceToFrontend(point.face) as VehicleFace,
                  driverId: data.driverId,
                })),
              };
            }
          );

          setDegradations({
            count: data.finalState.degradations.count || 0,
            details: data.finalState.degradations.details || {
              rayures: 0,
              chocs: 0,
              manquant: 0,
              casse: 0,
            },
            images,
          });
        } else if (!isReturn && data.initialState?.degradations) {
          // Phase d'entrée - charger uniquement les dégradations d'entrée
          console.log(
            "[ExteriorInspection] Chargement des dégradations d'ENTRÉE"
          );

          const images = (data.initialState.degradations.images || []).map(
            (img) => {
              // Si l'image contient déjà les nouvelles propriétés
              if (img.template && img.position) {
                // Convertir la valeur API de la face vers le nom frontend
                const frontendFace = mapBackendFaceToFrontend(img.face);

                return {
                  url: img.url,
                  damageType: img.damageType as DamageType,
                  face: frontendFace as VehicleFace,
                  template: img.template,
                  position: img.position,
                  driverId: img.driverId || data.driverId,
                  points: img.points || [
                    {
                      x: img.position.x,
                      y: img.position.y,
                      type: img.damageType as DamageType,
                      face: frontendFace as VehicleFace,
                      driverId: img.driverId || data.driverId,
                    },
                  ],
                };
              }

              // Conversion du format ancien
              // Convertir la valeur API de la face vers le nom frontend
              const frontendFace = mapBackendFaceToFrontend(img.face);

              return {
                url: img.url,
                damageType: img.damageType as DamageType,
                face: frontendFace as VehicleFace,
                template:
                  vehicleFaces.find((f) => f.name === frontendFace)?.url || "",
                position: img.points?.[0]
                  ? { x: img.points[0].x, y: img.points[0].y }
                  : undefined,
                driverId: data.driverId,
                points: (img.points || []).map((point) => ({
                  x: point.x,
                  y: point.y,
                  type: point.type as DamageType,
                  face: mapBackendFaceToFrontend(point.face) as VehicleFace,
                  driverId: data.driverId,
                })),
              };
            }
          );

          setDegradations({
            count: data.initialState.degradations.count || 0,
            details: data.initialState.degradations.details || {
              rayures: 0,
              chocs: 0,
              manquant: 0,
              casse: 0,
            },
            images,
          });
        } else {
          // Si aucune dégradation n'existe pour cette phase, initialiser avec des valeurs vides
          console.log(
            `[ExteriorInspection] Aucune dégradation trouvée pour la phase de ${
              isReturn ? "RETOUR" : "ENTRÉE"
            }, initialisation à vide`
          );

          setDegradations({
            count: 0,
            details: {
              rayures: 0,
              chocs: 0,
              manquant: 0,
              casse: 0,
            },
            images: [],
          });
        }
      } catch (err) {
        console.error("Erreur lors du chargement de l'affectation:", err);
        setError("Impossible de charger les détails de l'affectation");
        enqueueSnackbar("Erreur lors du chargement de l'affectation", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [id, enqueueSnackbar, isReturn]);

  // Ajouter une fonction pour obtenir l'icône selon le type de dégât
  const getDamageIcon = (type: DamageType) => {
    // Normaliser le type pour gérer les variantes avec/sans accent
    const normalizedType = type === "Cassé" ? "Casse" : type;

    switch (normalizedType) {
      case "Rayure":
        return <ScatterPlotIcon sx={{ color: "white", fontSize: 16 }} />;
      case "Choc":
        return <WarningIcon sx={{ color: "white", fontSize: 16 }} />;
      case "Manquant":
        return <ErrorOutlineIcon sx={{ color: "white", fontSize: 16 }} />;
      case "Casse":
        return <BuildIcon sx={{ color: "white", fontSize: 16 }} />;
      default:
        return <DeleteIcon sx={{ color: "white", fontSize: 16 }} />;
    }
  };

  // Ajouter une fonction pour obtenir la couleur selon le type de dégât
  const getDamageColor = (type: DamageType) => {
    // Normaliser le type pour gérer les variantes avec/sans accent
    const normalizedType = type === "Cassé" ? "Casse" : type;

    switch (normalizedType) {
      case "Rayure":
        return "#FFA500"; // Orange
      case "Choc":
        return "#FF0000"; // Rouge
      case "Manquant":
        return "#0000FF"; // Bleu
      case "Casse":
        return "#800080"; // Violet
      default:
        return "#FF0000"; // Rouge par défaut
    }
  };

  // Modifier la fonction handleImageClick pour stocker les informations dans le nouveau format
  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    if (!selectedFace || !selectedDamageType || !assignment) return;

    const image = event.currentTarget;
    const rect = image.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    const templateUrl =
      vehicleFaces.find((f) => f.name === selectedFace)?.url || "";

    console.log("[ExteriorInspection] Ajout d'un nouveau dégât:", {
      face: selectedFace,
      type: selectedDamageType,
      position: { x, y },
      template: templateUrl,
      driverId: assignment.driverId,
    });

    // Pour rétrocompatibilité, on garde aussi le point dans le format ancien
    const newPoint: DamagePoint = {
      x,
      y,
      type: selectedDamageType as DamageType,
      face: selectedFace as VehicleFace,
      driverId: assignment.driverId,
    };

    // Mettre à jour les dégradations
    setDegradations((prev) => {
      // Mettre à jour le compteur de dégâts
      const updatedDetails = { ...prev.details };
      const damageKey =
        selectedDamageType.toLowerCase() as keyof typeof prev.details;
      updatedDetails[damageKey] = (updatedDetails[damageKey] || 0) + 1;

      console.log("[ExteriorInspection] Mise à jour des compteurs:", {
        details: updatedDetails,
        count: prev.count + 1,
      });

      // Toujours créer une nouvelle entrée pour chaque dégât avec le nouveau format
      return {
        ...prev,
        details: updatedDetails,
        count: prev.count + 1,
        images: [
          ...prev.images,
          {
            url: templateUrl, // URL de l'image réelle (à remplacer plus tard)
            damageType: selectedDamageType as DamageType,
            face: selectedFace as VehicleFace,
            template: templateUrl,
            position: { x, y },
            driverId: assignment.driverId,
            points: [newPoint], // Pour rétrocompatibilité
          },
        ],
      };
    });
  };

  // Modifier la fonction handleRemoveDamage pour mettre à jour correctement les compteurs
  const handleRemoveDamage = (face: string, pointIndex: number) => {
    setDegradations((prev) => {
      // Trouver tous les index d'images correspondant à la face sélectionnée
      const imgIndexes = prev.images
        .map((img, index) => (img.face === face ? index : -1))
        .filter((idx) => idx !== -1);

      if (imgIndexes.length === 0 || pointIndex >= imgIndexes.length)
        return prev;

      // Index de l'image à supprimer
      const imgIndexToRemove = imgIndexes[pointIndex];
      const imgToRemove = prev.images[imgIndexToRemove];

      if (!imgToRemove) return prev;

      // Mettre à jour le compteur de dégâts
      const updatedDetails = { ...prev.details };
      const damageKey =
        imgToRemove.damageType.toLowerCase() as keyof typeof prev.details;
      updatedDetails[damageKey] = Math.max(
        0,
        (updatedDetails[damageKey] || 0) - 1
      );

      // Filtrer l'image à supprimer
      const updatedImages = prev.images.filter(
        (_, index) => index !== imgIndexToRemove
      );

      console.log("[ExteriorInspection] Suppression d'un dégât:", {
        face,
        pointIndex,
        imgIndexToRemove,
        damageKey,
        updatedDetails,
      });

      return {
        ...prev,
        details: updatedDetails,
        count: Math.max(0, prev.count - 1),
        images: updatedImages,
      };
    });
  };

  // Gérer le workflow RAS (Rien À Signaler) - Mode rapide
  const handleQuickRAS = async () => {
    console.log('🚀 Workflow RAS démarré');

    if (!id || !assignment) {
      console.error('❌ Paramètres manquants');
      return;
    }

    setRasSubmitting(true);

    try {
      // Étape 1: Dégradations
      console.log('🔧 Étape 1/3: Inspection extérieure');
      const resp1 = await axios.patch(`${API_URL}/assignments/${id}/degradations`, {
        count: 0,
        details: { rayures: 0, chocs: 0, manquant: 0, casse: 0 },
        images: []
      });
      console.log('📊 Après dégradations, currentStep:', resp1.data?.currentStep);

      // Étape 2: Propreté
      console.log('🧽 Étape 2/3: Propreté');
      const resp2 = await axios.patch(`${API_URL}/assignments/${id}/cleanliness`, {
        interior: true,
        exterior: true,
        cabine: true,
        comments: "RAS - Mode rapide"
      });
      console.log('📊 Après propreté, currentStep:', resp2.data?.currentStep);

      // Étape 3: Checklist
      console.log('📋 Étape 3/3: Checklist');
      const resp3 = await axios.patch(`${API_URL}/assignments/${id}/checklist`, {
        lights: "conforme",
        brakes: "conforme",
        tires: "conforme",
        documents: "conforme",
        comments: "RAS - Mode rapide"
      });
      console.log('📊 Après checklist, currentStep:', resp3.data?.currentStep);

      // Vérifier que l'étape est bien signatures_entree ou signatures_retour
      const expectedStep = isReturn ? 'signatures_retour' : 'signatures_entree';
      const actualStep = resp3.data?.currentStep;
      
      if (actualStep !== expectedStep) {
        console.warn(`⚠️ Étape actuelle (${actualStep}) différente de l'attendue (${expectedStep})`);
      }

      console.log('✅ Workflow RAS terminé - redirection signatures');
      enqueueSnackbar("✅ Inspection rapide terminée", { variant: "success" });

      // Rediriger vers les signatures
      const signatureUrl = isReturn ? `/signatures/${id}?return=true` : `/signatures/${id}`;
      navigate(signatureUrl);
    } catch (error: any) {
      console.error("❌ Erreur workflow RAS:", error?.response?.data || error);
      enqueueSnackbar("Erreur lors du workflow RAS", { variant: "error" });
    } finally {
      setRasSubmitting(false);
      setShowRASConfirmation(false);
      setActiveDialog(null);
    }
  };

  // Mettre à jour handleSubmit pour adapter les données au nouveau format API
  const handleSubmit = async () => {
    if (!id || !assignment) return;

    // Validation selon le mode d'inspection
    if (inspectionMode === 'complete') {
      const totalDamages = degradations.count;
      const totalPhotos = degradations.images.length;

      if (totalDamages > 0 && totalPhotos === 0) {
        enqueueSnackbar('Mode complet : au moins une photo est requise pour chaque dégât signalé', { variant: 'error' });
        return;
      }

      if (totalDamages > totalPhotos) {
        enqueueSnackbar(`Mode complet : ${totalDamages} dégâts signalés mais seulement ${totalPhotos} photos prises`, { variant: 'error' });
        return;
      }
    }

    setSubmitting(true);
    try {
      // Construction des images au format attendu par l'API
      const imagesApi = degradations.images.flatMap((img) => {
        // Si l'image a déjà la nouvelle structure (position et template)
        if (img.position && img.template) {
          return [
            {
              url: img.url,
              // Normaliser le type de dégât pour correspondre à l'enum du backend
              damageType: img.damageType === "Cassé" ? "Casse" : img.damageType,
              face: mapFrontendFaceToBackend(img.face),
              template: img.template,
              // S'assurer que la position est bien formatée
              position: {
                x:
                  typeof img.position.x === "number"
                    ? Math.min(100, Math.max(0, img.position.x))
                    : 50,
                y:
                  typeof img.position.y === "number"
                    ? Math.min(100, Math.max(0, img.position.y))
                    : 50,
              },
              driverId: img.driverId || assignment.driverId,
            },
          ];
        }
        // Sinon, convertir depuis l'ancien format avec points
        return (img.points || []).map((point: DamagePoint) => ({
          url: img.url,
          // Normaliser le type de dégât pour correspondre à l'enum du backend
          damageType: point.type === "Cassé" ? "Casse" : point.type,
          face: mapFrontendFaceToBackend(point.face),
          template: vehicleFaces.find((f) => f.name === point.face)?.url || "",
          // S'assurer que la position est bien formatée
          position: {
            x:
              typeof point.x === "number"
                ? Math.min(100, Math.max(0, point.x))
                : 50,
            y:
              typeof point.y === "number"
                ? Math.min(100, Math.max(0, point.y))
                : 50,
          },
          driverId: point.driverId || assignment.driverId,
        }));
      });

      // Normaliser les détails des dégâts
      const normalizedDetails = {
        rayures: degradations.details.rayures || 0,
        chocs: degradations.details.chocs || 0,
        manquant: degradations.details.manquant || 0,
        casse:
          (degradations.details as any).cassé ||
          degradations.details.casse ||
          0,
      };

      const degradationsApi = {
        ...degradations,
        details: normalizedDetails,
        images: imagesApi,
      };

      console.log(
        "[ExteriorInspection] Données normalisées envoyées à l'API:",
        degradationsApi
      );

      // Vérifier si l'étape actuelle est correcte
      const currentStep = isReturn
        ? "inspection_exterieure_retour"
        : "inspection_exterieure";

      if (assignment.currentStep !== currentStep) {
        console.log(
          "[ExteriorInspection] Tentative de mise à jour de l'étape:",
          {
            de: assignment.currentStep,
            vers: currentStep,
          }
        );

        try {
          // Mettre à jour l'étape en utilisant nextStep
          let updatedAssignment = assignment;
          while (updatedAssignment.currentStep !== currentStep) {
            updatedAssignment = await AssignmentService.nextStep(id);
            console.log(
              "[ExteriorInspection] Étape mise à jour:",
              updatedAssignment.currentStep
            );
          }
          console.log(
            "[ExteriorInspection] Étape finale atteinte:",
            updatedAssignment
          );
          setAssignment(updatedAssignment);
        } catch (updateError) {
          console.error(
            "[ExteriorInspection] Erreur lors de la mise à jour de l'étape:",
            updateError
          );
          enqueueSnackbar(
            "Impossible de mettre à jour l'étape. Veuillez réessayer.",
            { variant: "error" }
          );
          return;
        }
      }

      if (isReturn) {
        await AssignmentService.addDegradations(id, degradationsApi, true);
        enqueueSnackbar("Dégradations finales enregistrées avec succès", {
          variant: "success",
        });
      } else {
        await AssignmentService.addDegradations(id, degradationsApi);
        enqueueSnackbar("Dégradations initiales enregistrées avec succès", {
          variant: "success",
        });
      }

      const finalAssignment = await AssignmentService.nextStep(id);
      console.log(
        "[ExteriorInspection] Affectation mise à jour:",
        finalAssignment
      );
      setAssignment(finalAssignment);

      if (isReturn) {
        navigate(`/inspection-proprete/${id}?return=true`);
      } else {
        navigate(`/inspection-proprete/${id}`);
      }
    } catch (err) {
      console.error(
        "[ExteriorInspection] Erreur lors de l'enregistrement des dégradations:",
        err
      );
      enqueueSnackbar(
        (err as any)?.response?.data?.message ||
          "Erreur lors de l'enregistrement des dégradations",
        { variant: "error" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f9f9f9",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !assignment) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          backgroundColor: "#f9f9f9",
          py: { xs: 2, md: 3 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1600 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" color="error">
              {error || "Affectation non trouvée"}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/assignments")}
              sx={{ mt: 2 }}
            >
              Retour à la liste
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        backgroundColor: "#f9f9f9",
        py: { xs: 2, md: 3 },
        px: { xs: 1, md: 2 },
      }}
    >
      {/* ✨ SÉLECTEUR DE MODE D'INSPECTION */}
      <Dialog
        open={activeDialog === 'modes' && showModeSelector && inspectionMode === null}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem' }}>
          🔍 Choisissez le mode d'inspection
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Mode RAPIDE */}
            <Box
              onClick={() => {
                console.log('⚡ MODE RAPIDE sélectionné');
                setInspectionMode('quick');
                setShowModeSelector(false);
                setActiveDialog('ras');
                setShowRASConfirmation(true);
                enqueueSnackbar('Mode rapide activé', { variant: 'info' });
              }}
              sx={{
                p: 3,
                border: '2px solid',
                borderColor: 'success.main',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'success.50',
                  transform: 'scale(1.02)',
                  boxShadow: 3
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{ fontSize: '2rem' }}>⚡</Box>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  RAPIDE (30 sec)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                • Affectations habituelles<br/>
                • Question simple : nouveaux dégâts ? Oui/Non<br/>
                • Validation en 1 clic si RAS
              </Typography>
            </Box>

            {/* Mode STANDARD */}
            <Box
              onClick={() => {
                console.log('📋 MODE STANDARD sélectionné');
                setInspectionMode('standard');
                setShowModeSelector(false);
                setActiveDialog(null);
                enqueueSnackbar('Mode standard activé : inspection normale', { variant: 'info' });
              }}
              sx={{
                p: 3,
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'primary.50',
                  transform: 'scale(1.02)',
                  boxShadow: 3
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{ fontSize: '2rem' }}>📋</Box>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  STANDARD (2-3 min)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                • Inspection normale recommandée<br/>
                • Vérification visuelle rapide<br/>
                • Dégâts connus affichés en gris
              </Typography>
            </Box>

            {/* Mode COMPLET */}
            <Box
              onClick={() => {
                console.log('🔍 MODE COMPLET sélectionné');
                setInspectionMode('complete');
                setShowModeSelector(false);
                setActiveDialog(null);
                enqueueSnackbar('Mode complet activé : photos obligatoires pour tous dégâts', { variant: 'warning' });
              }}
              sx={{
                p: 3,
                border: '2px solid',
                borderColor: 'warning.main',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'warning.50',
                  transform: 'scale(1.02)',
                  boxShadow: 3
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{ fontSize: '2rem' }}>🔍</Box>
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  COMPLET (5 min)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                • Premier du mois, véhicule sensible<br/>
                • Inspection approfondie requise<br/>
                • Photos obligatoires
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ✨ DIALOGUE RAS - Mode rapide */}
      <Dialog
        open={activeDialog === 'ras'}
        onClose={() => {
          console.log('❌ Dialogue RAS fermé');
          setShowRASConfirmation(false);
          setActiveDialog(null);
        }}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem', bgcolor: 'success.main', color: 'white' }}>
          ⚡ Mode Rapide Activé
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              🚗 Véhicule en bon état ?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Si le véhicule est conforme (RAS - Rien À Signaler),<br/>
              toutes les étapes seront validées automatiquement.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={() => {
                console.log('✅ RAS OUI cliqué - lancement workflow');
                handleQuickRAS();
              }}
              disabled={rasSubmitting}
              startIcon={rasSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              sx={{ 
                py: 2, 
                fontSize: '1.1rem',
                fontWeight: 700,
                boxShadow: 3
              }}
            >
              {rasSubmitting ? "Validation en cours..." : "✅ OUI - Tout est conforme (RAS)"}
            </Button>

            <Button
              variant="outlined"
              color="warning"
              size="large"
              onClick={() => {
                console.log('⚠️ RAS NON cliqué - retour inspection');
                setShowRASConfirmation(false);
                setActiveDialog(null);
                enqueueSnackbar('Veuillez inspecter et signaler les dégâts', { variant: 'warning' });
              }}
              disabled={rasSubmitting}
              sx={{ py: 2, fontSize: '1.1rem', fontWeight: 700 }}
            >
              ⚠️ NON - Il y a des dégâts
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Container maxWidth={false} sx={{ maxWidth: 1600 }}>
        {/* BANDEAU MODE D'INSPECTION ACTIF */}
        {inspectionMode && (
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor:
                inspectionMode === 'quick' ? 'success.main' :
                inspectionMode === 'standard' ? 'primary.main' :
                'warning.main',
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ fontSize: '2rem' }}>
                  {inspectionMode === 'quick' ? '⚡' :
                   inspectionMode === 'standard' ? '📋' : '🔍'}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Mode {inspectionMode === 'quick' ? 'RAPIDE' :
                          inspectionMode === 'standard' ? 'STANDARD' : 'COMPLET'} actif
                  </Typography>
                  <Typography variant="body2">
                    {inspectionMode === 'quick' && 'Validation rapide en 1 clic si RAS'}
                    {inspectionMode === 'standard' && 'Inspection visuelle - signalement libre des dégâts'}
                    {inspectionMode === 'complete' && '⚠️ Photos OBLIGATOIRES pour chaque dégât signalé'}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setInspectionMode(null);
                  setShowModeSelector(true);
                  setActiveDialog('modes');
                }}
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Changer de mode
              </Button>
            </Box>
          </Paper>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? 2 : 0,
            mb: 4,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/assignments")}
            sx={{ mr: 2 }}
            size={isMobile ? "medium" : "large"}
          >
            Retour
          </Button>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.8rem", md: "2.2rem" },
            }}
          >
            {isReturn
              ? "Inspection Extérieure (Retour)"
              : "Inspection Extérieure"}
          </Typography>
        </Box>

        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 4,
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.2rem", md: "1.4rem" },
              }}
            >
              Information sur l'affectation
            </Typography>
            <Grid container spacing={2}>
              <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                <Typography variant="body1">
                  <strong>Véhicule:</strong> {assignment.vehiclePlate}
                </Typography>
              </Grid>
              <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                <Typography variant="body1">
                  <strong>Chauffeur:</strong> {assignment.driverFirstName}{" "}
                  {assignment.driverLastName}
                </Typography>
              </Grid>
              <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                <Typography variant="body1">
                  <strong>Date:</strong>{" "}
                  {new Date(assignment.date).toLocaleDateString("fr-FR")}
                </Typography>
              </Grid>
              <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                <Typography variant="body1">
                  <strong>Kilométrage:</strong> {assignment.startMileage} km
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.2rem", md: "1.4rem" },
              }}
            >
              Sélection de la face et du type de dégât
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Face du véhicule
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 3,
                }}
              >
                {vehicleFaces.map((face) => (
                  <Button
                    key={face.name}
                    variant={
                      selectedFace === face.name ? "contained" : "outlined"
                    }
                    onClick={() => setSelectedFace(face.name)}
                    sx={{
                      minWidth: 120,
                      height: 40,
                      borderRadius: 2,
                      borderWidth: 2,
                      "&:hover": {
                        borderWidth: 2,
                      },
                    }}
                  >
                    {face.name}
                  </Button>
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Type de dégât
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {damageTypes.map((type) => (
                  <Button
                    key={type}
                    variant={
                      selectedDamageType === type ? "contained" : "outlined"
                    }
                    onClick={() => setSelectedDamageType(type)}
                    startIcon={getDamageIcon(type as DamageType)}
                    sx={{
                      minWidth: 120,
                      height: 40,
                      borderRadius: 2,
                      borderWidth: 2,
                      backgroundColor:
                        selectedDamageType === type
                          ? getDamageColor(type as DamageType)
                          : "transparent",
                      borderColor: getDamageColor(type as DamageType),
                      color:
                        selectedDamageType === type
                          ? "white"
                          : getDamageColor(type as DamageType),
                      "&:hover": {
                        borderWidth: 2,
                        backgroundColor:
                          selectedDamageType === type
                            ? getDamageColor(type as DamageType)
                            : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    {type}
                  </Button>
                ))}
              </Box>
            </Box>

            {selectedFace && selectedDamageType && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderRadius: 1,
                }}
              >
                Cliquez directement sur l'image pour ajouter un dégât. Pour
                ajouter un autre type de dégât, sélectionnez simplement un
                nouveau type.
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.2rem", md: "1.4rem" },
              }}
            >
              Vue du véhicule
            </Typography>
            {selectedFace && (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                <img
                  ref={imageRef}
                  src={vehicleFaces.find((f) => f.name === selectedFace)?.url}
                  alt={selectedFace}
                  style={{
                    width: "100%",
                    height: "auto",
                    cursor: selectedDamageType ? "crosshair" : "default",
                  }}
                  onClick={handleImageClick}
                />

                {/* Afficher les points de dégâts existants */}
                {degradations.images
                  .filter((img) => img.face === selectedFace)
                  .map((img, imgIndex) => (
                    <Box
                      key={`damage-${imgIndex}`}
                      sx={{
                        position: "absolute",
                        left: `${img.position?.x || 0}%`,
                        top: `${img.position?.y || 0}%`,
                        transform: "translate(-50%, -50%)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: getDamageColor(img.damageType),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          "&:hover": {
                            transform: "scale(1.1)",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                          },
                        }}
                        onClick={() => {
                          // Trouver l'index de l'image
                          const faceImages = degradations.images.filter(
                            (i) => i.face === selectedFace
                          );
                          const imgIndex = faceImages.findIndex(
                            (i) => i === img
                          );

                          handleRemoveDamage(selectedFace, imgIndex);
                        }}
                      >
                        {getDamageIcon(img.damageType)}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {img.driverId}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.2rem", md: "1.4rem" },
              }}
            >
              Résumé des dégâts
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              {damageTypes.map((type) => (
                <Paper
                  key={type}
                  elevation={2}
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "white",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: getDamageColor(type as DamageType),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                    }}
                  >
                    {getDamageIcon(type as DamageType)}
                  </Box>
                  <Typography
                    variant="h4"
                    color="text.primary"
                    sx={{ fontWeight: "bold" }}
                  >
                    {degradations.details[
                      type.toLowerCase() as keyof typeof degradations.details
                    ] || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {type}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate("/assignments")}
            fullWidth={isMobile}
          >
            Annuler
          </Button>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: isMobile ? "column" : "row",
              flexWrap: 'wrap'
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/assignments")}
              disabled={submitting}
              fullWidth={isMobile}
            >
              Étape précédente
            </Button>
            
            {/* ✨ BOUTON RAS - Mode rapide */}
            {inspectionMode === 'quick' && isReturn && (
              <Button
                variant="contained"
                color="success"
                onClick={() => setShowRASConfirmation(true)}
                disabled={submitting || rasSubmitting}
                fullWidth={isMobile}
                sx={{
                  backgroundColor: 'success.main',
                  '&:hover': { backgroundColor: 'success.dark' },
                  fontWeight: 'bold'
                }}
              >
                ✓ RAS - Tout est OK
              </Button>
            )}
            
            {/* Rappel mode complet */}
            {inspectionMode === 'complete' && degradations.count > 0 && (
              <Alert severity="warning" sx={{ mr: 2 }}>
                📸 Mode complet : {degradations.count} dégât(s) signalé(s), {degradations.images.length} photo(s) requise(s)
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting}
              fullWidth={isMobile}
            >
              {submitting ? "Enregistrement..." : "Continuer"}
            </Button>
          </Box>
          
        </Box>

        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
          {damageTypes.map((type) => (
            <Box
              key={type}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: getDamageColor(type as DamageType),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {getDamageIcon(type as DamageType)}
              </Box>
              <Typography variant="body2">{type}</Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ExteriorInspection;
